import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { PencilSimpleIcon } from '@phosphor-icons/react/dist/csr/PencilSimple';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';
import { TrashIcon } from '@phosphor-icons/react/dist/csr/Trash';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { PageActions } from '../../app/shell/PageActions';

import { TemplateEditor } from './TemplateEditor';
import { AttributeTag } from '../../design-system/primitives/AttributeTag/AttributeTag';
import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { ClientDataTable } from '../../design-system/composites/DataTable/ClientDataTable';
import {
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import {
  isStructuralTemplateCode,
  smsApi,
  smsTemplateManagementApi,
  type SmsTemplate,
} from '../../lib/api/sms';
import styles from './TemplateCatalogue.module.css';

/**
 * The SMS template management screen (M-18, Task 6) — `GET
 * /sms-templates/catalogue` plus create/edit/delete, gated
 * `sms.template.manage` entirely by `App.tsx`'s own route guard and
 * `navigation.ts`'s nav-entry filter (the identical `CodeScreen` shape):
 * ALL SIX endpoints behind this screen require that one permission,
 * including the catalogue read itself, so unlike `DispensaryList` there is
 * no partial "can view but not manage" state for this component to render
 * — reaching this screen at all already means every action on it is
 * available. No `permissions` prop, for that reason.
 *
 * ## "Active" is DERIVED, not read off the wire (a confirmed API gap)
 *
 * `SmsTemplateResource` — verified against the API repository, all FIVE
 * call sites that reuse it (`SmsTemplateController::index()`,
 * `SmsTemplateCatalogueController::__invoke()`,
 * `SmsTemplateWriteController::store()`/`::update()`, and
 * `SmsTemplateVersionController::revert()`) — never emits `is_active` at
 * all: it emits exactly nine keys (`uuid, code, name, description,
 * channel, locale, body, variables, category`), none of them this one.
 * The OpenAPI `SmsTemplate` schema's `additionalProperties: false` with
 * that same nine-key `required` list confirms this is not one call site's
 * oversight. Rather than guess a tenth field the wire does not carry,
 * this screen combines TWO already-verified endpoints it already has to
 * know about: `GET /sms-templates` (the send picker, `active()`-only)
 * tells it which `uuid`s are currently active, and `GET
 * /sms-templates/catalogue` (every non-deleted row) is the list itself. A
 * `uuid` in the picker's set IS "active" by the server's own definition
 * (`SmsTemplate::scopeActive()`).
 *
 * This costs one extra request per load and depends on the caller also
 * holding `sms.view` — true for every role that holds `sms.template.manage`
 * today (`RolePermissionSeeder::MATRIX`, verified: only `administrator`
 * holds the latter, and it holds the former too) but not guaranteed for a
 * future role. If the picker call fails for any reason, active state
 * degrades to "unknown" (an em dash) rather than failing the whole screen
 * — see `load()` below. `TemplateEditor` is handed this derived value as
 * `knownIsActive` (fix round 1, C-1) precisely because it is the only
 * source of truth for "is this template active" anywhere in this
 * screen — a bare `SmsTemplate` handed to the editor would leave it no
 * better informed than the wire itself.
 */

export interface TemplateCatalogueRow extends Row, SmsTemplate {
  /** `null` means unknown — the picker call failed or has not resolved. */
  is_active: boolean | null;
  is_structural: boolean;
}

function toRow(template: SmsTemplate, activeUuids: ReadonlySet<string> | null): TemplateCatalogueRow {
  return {
    ...template,
    id: template.uuid,
    is_active: activeUuids === null ? null : activeUuids.has(template.uuid),
    is_structural: isStructuralTemplateCode(template.code),
  };
}

const FORBIDDEN_CODE = 'forbidden';

export function TemplateCatalogue() {
  const { t } = useTranslation();

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly TemplateCatalogueRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  /**
   * The whole ROW, not a bare `SmsTemplate` (fix round 1, C-1) — the row
   * carries `is_active`, which is the only place this screen ever learns
   * it (this module's own docblock), and `TemplateEditor` needs it as
   * `knownIsActive` to avoid silently reactivating a retired template on
   * an unrelated edit.
   */
  const [editingTemplate, setEditingTemplate] = useState<TemplateCatalogueRow | undefined>(undefined);

  const [pendingDelete, setPendingDelete] = useState<TemplateCatalogueRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteFailure, setDeleteFailure] = useState<string | null>(null);
  const [deleteReference, setDeleteReference] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      const all = await smsTemplateManagementApi.catalogue();

      let activeUuids: ReadonlySet<string> | null = null;
      try {
        const active = await smsApi.templates();
        activeUuids = new Set(active.map((row) => row.uuid));
      } catch (error) {
        if (!(error instanceof ApiError)) throw error;
        // Active state degrades to "unknown" — see this module's own docblock.
      }

      const mapped = all.map((template) => toRow(template, activeUuids));
      setRows(mapped);
      setStatus(mapped.length === 0 ? 'empty' : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate(): void {
    setEditingTemplate(undefined);
    setEditorOpen(true);
  }

  function openEdit(template: TemplateCatalogueRow): void {
    setEditingTemplate(template);
    setEditorOpen(true);
  }

  function closeEditor(): void {
    setEditorOpen(false);
  }

  /**
   * `appliedIsActive` is the EDITOR's OWN bookkeeping of what the request
   * it just sent actually SET — never a re-read of the wire, which cannot
   * answer this (this module's own docblock), and never a display-only
   * guess. It is `null` exactly when the edit sent no `is_active` at all
   * (an untouched checkbox on a non-structural row — see
   * `TemplateEditor.tsx`'s own `onSaved` docblock, fix round 2, n-1): in
   * that case nothing about this row's active state changed, so the row
   * KEEPS its previous `is_active` (which may itself still be the unknown
   * `null` the picker degraded to) rather than the display claiming a
   * state — "Active" — that nobody asked for and the PATCH never sent.
   * A create the editor never toggles is active by default
   * (`StoreSmsTemplateRequest::fields()`, verified) and always reports a
   * real value here, never `null`.
   */
  function handleSaved(saved: SmsTemplate, appliedIsActive: boolean | null): void {
    setRows((current) => {
      const exists = current.some((row) => row.id === saved.uuid);
      return exists
        ? current.map((row) =>
            row.id === saved.uuid
              ? {
                  ...row,
                  ...saved,
                  is_active: appliedIsActive ?? row.is_active,
                  is_structural: isStructuralTemplateCode(saved.code),
                }
              : row,
          )
        : [
            ...current,
            {
              ...saved,
              id: saved.uuid,
              is_active: appliedIsActive ?? true,
              is_structural: isStructuralTemplateCode(saved.code),
            },
          ];
    });
    if (status === 'empty') setStatus('ready');
    setEditorOpen(false);
  }

  function describeDeleteFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        // `SmsTemplateWriteController::destroy()` throws its
        // `AuthorizationException` for exactly one reason — the template
        // is structural (fix round 1, m-3: this branch used to also
        // catch a `conflict` code that this endpoint never actually
        // answers, mislabelling a DIFFERENT kind of refusal as
        // "structural"). `error.message` is not localised (`client.ts`'s
        // own rule), so this branches to fixed copy rather than
        // rendering it. In practice this delete confirmation is only
        // ever reachable for a NON-structural row (`renderActions`
        // offers no Delete control for one that is), so this branch is
        // defence in depth against a structural flag that changed
        // between load and confirm, not a path this UI's own controls
        // can provoke.
        return t('smsTemplates.deleteRefusedStructural');
      case TOO_MANY_REQUESTS_CODE:
        return t('smsTemplates.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('smsTemplates.deleteFailed');
    }
  }

  function openDeleteConfirm(row: TemplateCatalogueRow): void {
    setPendingDelete(row);
    setDeleteFailure(null);
    setDeleteReference(null);
  }

  function closeDeleteConfirm(): void {
    setPendingDelete(null);
    setDeleteFailure(null);
    setDeleteReference(null);
  }

  async function confirmDelete(): Promise<void> {
    if (pendingDelete === null || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteFailure(null);
    setDeleteReference(null);

    try {
      await smsTemplateManagementApi.deleteTemplate(pendingDelete.uuid);
      setRows((current) => {
        const next = current.filter((row) => row.id !== pendingDelete.uuid);
        // Fix round 1, m-7. `handleSaved` already moves `'empty' ->
        // `'ready'` on the reverse transition; this is the missing other
        // direction — without it, deleting the last row left `status`
        // at `'ready'` with zero rows: a header over an empty body,
        // never the "No templates yet." empty state.
        if (next.length === 0) setStatus('empty');
        return next;
      });
      closeDeleteConfirm();
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setDeleteFailure(describeDeleteFailure(error));
      setDeleteReference(error.requestId ?? null);
    } finally {
      setDeleteBusy(false);
    }
  }

  function renderActiveState(row: Row): ReactNode {
    const active = (row as unknown as TemplateCatalogueRow).is_active;
    if (active === null) return <span data-testid="templateActiveUnknown">—</span>;
    return active ? t('smsTemplates.statusActive') : t('smsTemplates.statusInactive');
  }

  function renderStructural(row: Row): ReactNode {
    const structural = (row as unknown as TemplateCatalogueRow).is_structural;
    return structural ? (
      <AttributeTag tone="outline">{t('smsTemplates.structural')}</AttributeTag>
    ) : null;
  }

  function renderActions(row: Row): ReactNode {
    const template = row as unknown as TemplateCatalogueRow;
    return (
      <div className={styles['rowActions']}>
        <Button variant="ghost" onClick={() => openEdit(template)} icon={<PencilSimpleIcon size={16} />}>
          {t('smsTemplates.edit')}
        </Button>
        {/*
          A STRUCTURAL TEMPLATE OFFERS NO DELETE CONTROL AT ALL — the API
          refuses it unconditionally (`SmsTemplateWriteController::
          destroy()`), and a button that always fails is worse than one
          that is not there (this task's own brief).
        */}
        {template.is_structural ? null : (
          <Button variant="ghost" onClick={() => openDeleteConfirm(template)} icon={<TrashIcon size={16} />}>
            {t('smsTemplates.delete')}
          </Button>
        )}
      </div>
    );
  }

  const columns: readonly Column[] = [
    { key: 'code', label: t('smsTemplates.columnCode'), neverMirrors: true },
    { key: 'name', label: t('smsTemplates.columnName') },
    { key: 'category', label: t('smsTemplates.columnCategory') },
    { key: 'locale', label: t('smsTemplates.columnLocale'), neverMirrors: true },
    { key: 'active', label: t('smsTemplates.columnActive'), render: renderActiveState },
    { key: 'structural', label: t('smsTemplates.columnStructural'), render: renderStructural },
    { key: 'actions', label: t('smsTemplates.columnActions'), render: renderActions },
  ];

  return (
    <div className={styles['page']}>
      {/* No page subtitle under the shell's own <h1>; the screen's one
          command rides in the title row. */}
      <PageActions>
        <Button onClick={openCreate} icon={<PlusIcon size={16} />}>
          {t('smsTemplates.create')}
        </Button>
      </PageActions>

      <ClientDataTable
        caption={t('smsTemplates.title')}
        columns={columns}
        rows={rows}
        status={status}
        empty={{ body: t('smsTemplates.empty') }}
        error={{ title: t('smsTemplates.loadFailed') }}
      />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button
            variant="secondary"
            onClick={() => {
              void load();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('smsTemplates.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="templateCatalogueRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <TemplateEditor
        open={editorOpen}
        template={editingTemplate}
        knownIsActive={editingTemplate?.is_active ?? null}
        onClose={closeEditor}
        onSaved={handleSaved}
      />

      <Dialog
        open={pendingDelete !== null}
        destructive
        title={t('smsTemplates.confirmDeleteTitle', { name: pendingDelete?.name ?? '' })}
        onClose={closeDeleteConfirm}
        actions={
          <>
            <Button
              variant="destructive"
              loading={deleteBusy}
              onClick={() => {
                void confirmDelete();
              }}
              icon={<TrashIcon size={16} />}
            >
              {t('smsTemplates.confirm')}
            </Button>
            <Button variant="ghost" onClick={closeDeleteConfirm} icon={<XIcon size={16} />}>
              {t('smsTemplates.cancel')}
            </Button>
          </>
        }
      >
        <p className={styles['body']}>{t('smsTemplates.confirmDeleteBody', { name: pendingDelete?.name ?? '' })}</p>
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {deleteFailure === null ? null : <span className={styles['failureText']}>{deleteFailure}</span>}
          {deleteReference === null ? null : (
            <span className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="templateDeleteRequestId">
                {deleteReference}
              </bdi>
            </span>
          )}
        </div>
      </Dialog>
    </div>
  );
}
